// The window that shows a generation step by step, with the scenes appearing as they are designed.
// "Continue in background" closes it; the generation goes on (see AIJobsContext).

import React, { useEffect, useRef, useState } from "react";
import { planOf } from "../Utils/aiTemplate/aiApi";

const STEPS = [
  { key: "understand", label: "Understanding your idea" },
  { key: "write", label: "Writing your scenes" },
  { key: "design", label: "Designing the scenes" },
  { key: "presenter", label: "Adding presenter & animations" },
  { key: "save", label: "Saving to My Projects" },
];

/** Which step is running now (index in STEPS), STEPS.length when finished. */
function currentStep(job, plan) {
  if (job.status === "done" || job.step === "done") return STEPS.length;
  // Failed / cancelled: the step it stopped at.
  if (job.status === "failed" || job.status === "cancelled") return plan ? (job.step === "save" ? 4 : 2) : 1;
  if (job.step === "save") return 4;
  if (job.step === "presenter") return 3;
  if (job.status === "building" || job.status === "planned") return 2;
  if (job.status === "planning") return (job.progress || 0) < 25 ? 0 : 1;
  return -1; // queued
}

const thumbUrl = (api, path) => (path ? String(api).replace(/\/$/, "") + "/" + String(path).replace(/^\//, "") : null);

export default function AIProgressPanel({ job, apiBaseURL, onClose, onCancel: cancel, onOpen: openProject }) {
  const [selected, setSelected] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const timer = useRef(null);

  const plan = planOf(job);
  const previews = job.previews || [];
  const sceneCount = job.sceneCount || (plan && plan.scenes.length) || 4;
  const step = currentStep(job, plan);
  const done = job.status === "done";
  const failed = job.status === "failed" || job.status === "cancelled";
  const progress = done ? 100 : Math.max(2, Math.min(99, job.progress || 2));

  // Finished while the window was open: open the editor by itself after a short countdown
  // (unless the user stays here). Not when the window is opened on a finished template.
  const watched = useRef(job.status !== "done");
  useEffect(() => {
    if (done && watched.current && job.projectId && countdown === null) setCountdown(4);
  }, [done, job.projectId, countdown]);
  useEffect(() => {
    if (countdown === null || countdown === "off") return undefined;
    if (countdown <= 0) {
      openProject(job);
      return undefined;
    }
    timer.current = setTimeout(() => setCountdown((c) => (typeof c === "number" ? c - 1 : c)), 1000);
    return () => clearTimeout(timer.current);
  }, [countdown, job, openProject]);

  const shown = selected != null && previews[selected] ? selected : previews.length ? previews.length - 1 : null;
  const big = shown != null ? previews[shown] : done ? thumbUrl(apiBaseURL, job.previewImagePath) : null;

  const title = done
    ? "Your template is ready!"
    : failed
    ? job.status === "cancelled"
      ? "Generation cancelled"
      : "Something went wrong"
    : job.status === "queued"
    ? "Getting ready..."
    : "Creating your template";

  return (
    <div className="ais-overlay" onMouseDown={(e) => e.target === e.currentTarget && !done && onClose()}>
      <div className="ais-panel" role="dialog" aria-label="AI template progress">
        <div className={"ais-panel-head" + (done ? " is-done" : failed ? " is-failed" : "")}>
          <div className="ais-panel-glow" />
          <div className="ais-panel-title">
            <span className="material-icons ais-spark">{done ? "check_circle" : failed ? "error_outline" : "auto_awesome"}</span>
            <div>
              <div className="ais-panel-h">{title}</div>
              <div className="ais-panel-sub">
                &ldquo;{job.keyword}&rdquo;
                {plan && plan.title && !failed && plan.title.toLowerCase() !== String(job.keyword).toLowerCase() ? (
                  <> &middot; {plan.title}</>
                ) : null}
              </div>
            </div>
          </div>
          <button className="ais-icon-btn" onClick={onClose} title={done || failed ? "Close" : "Continue in background"}>
            <span className="material-icons">close</span>
          </button>
          {!failed && (
            <div className="ais-bar">
              <div className="ais-bar-fill" style={{ width: progress + "%" }} />
            </div>
          )}
        </div>

        <div className="ais-panel-body">
          <div className="ais-steps">
            {job.status === "queued" && (
              <div className="ais-step is-active">
                <span className="ais-step-dot"><span className="ais-spinner" /></span>
                <span>
                  Waiting in line
                  {job.queueAhead > 0 ? <em> &middot; {job.queueAhead} ahead of you</em> : null}
                </span>
              </div>
            )}
            {STEPS.map((s, i) => {
              const state = failed ? (i < step ? "done" : i === step ? "failed" : "todo") : i < step ? "done" : i === step ? "active" : "todo";
              return (
                <div key={s.key} className={"ais-step is-" + state}>
                  <span className="ais-step-dot">
                    {state === "done" && <span className="material-icons">check</span>}
                    {state === "active" && <span className="ais-spinner" />}
                    {state === "failed" && <span className="material-icons">close</span>}
                  </span>
                  <span>
                    {s.key === "presenter" && job.presenter === false ? "Adding animations" : s.label}
                    {s.key === "design" && state === "active" && previews.length > 0 && (
                      <em> &middot; scene {Math.min(previews.length + 1, sceneCount)} of {sceneCount}</em>
                    )}
                  </span>
                </div>
              );
            })}
            {failed && job.error && <div className="ais-error">{job.error}</div>}
          </div>

          <div className="ais-stage">
            <div className="ais-stage-main">
              {big ? (
                <img key={shown} className="ais-stage-img" src={big} alt="Scene preview" />
              ) : (
                <div className="ais-skeleton">
                  <div className="ais-sk-line w60" />
                  <div className="ais-sk-line w80 big" />
                  <div className="ais-sk-line w40" />
                  <div className="ais-sk-btn" />
                  <div className="ais-sk-figure" />
                  <div className="ais-sk-caption">
                    {failed ? "No preview" : plan ? "Designing your first scene..." : "The AI is writing your scenes..."}
                  </div>
                </div>
              )}
            </div>
            <div className="ais-strip">
              {Array.from({ length: sceneCount }).map((_, i) => (
                <button
                  key={i}
                  className={"ais-thumb" + (previews[i] ? " is-ready" : "") + (shown === i ? " is-selected" : "")}
                  onClick={() => previews[i] && setSelected(i)}
                  title={plan && plan.scenes[i] ? plan.scenes[i].headline || plan.scenes[i].layout : "Scene " + (i + 1)}
                >
                  {previews[i] ? <img src={previews[i]} alt={"Scene " + (i + 1)} /> : <span className="ais-thumb-n">{i + 1}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="ais-panel-foot">
          {done && (
            <>
              <div className="ais-foot-note">
                {typeof countdown === "number" ? <>Opening the editor in {countdown}...</> : <>Saved in My Projects.</>}
              </div>
              {typeof countdown === "number" && (
                <button className="ais-btn ghost" onClick={() => setCountdown("off")}>Stay here</button>
              )}
              <button className="ais-btn primary" onClick={() => openProject(job)}>
                <span className="material-icons">edit</span> Open in editor
              </button>
            </>
          )}
          {failed && (
            <>
              <div className="ais-foot-note">{job.status === "failed" ? "No credit was used." : ""}</div>
              <button className="ais-btn ghost" onClick={onClose}>Close</button>
            </>
          )}
          {!done && !failed && (
            <>
              <div className="ais-foot-note">You can keep working, we&rsquo;ll let you know when it&rsquo;s ready.</div>
              {job.status !== "building" && !confirmCancel && (
                <button className="ais-btn ghost" onClick={() => setConfirmCancel(true)}>Cancel</button>
              )}
              {confirmCancel && (
                <button className="ais-btn danger" onClick={() => cancel(job.id).then(() => setConfirmCancel(false))}>
                  Yes, cancel it
                </button>
              )}
              <button className="ais-btn primary" onClick={onClose}>
                <span className="material-icons">open_in_new</span> Continue in background
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
